import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronUp,
  Dumbbell,
  Loader2,
  MapPin,
  Save,
  Send,
  Sparkles,
  Target,
  Users,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { getUser } from '../../utils/helpers';
import { NODE_BACKEND_URL } from '../../utils/constant';
import { useTeam } from '../../lib/teamConfig';
import MuscleVisualizer from '../../components/MuscleVisualizer/MuscleVisualizer';

const FITNESS_LEVELS = [
  { id: 'beginner', label: 'Beginner', description: 'New to structured training' },
  { id: 'intermediate', label: 'Intermediate', description: 'Building consistency' },
  { id: 'advanced', label: 'Advanced', description: '3+ years of lifting' },
  { id: 'elite', label: 'Elite', description: 'High-performance athlete' },
];

const FOCUS_OPTIONS = [
  { id: 'strength', label: 'Strength' },
  { id: 'conditioning', label: 'Conditioning' },
  { id: 'mobility', label: 'Mobility' },
  { id: 'sport-specific', label: 'Sport Specific' },
];

const MOBILE_COACH_SYSTEM_PROMPT = `SYSTEM ROLE: SLAM Mobile Coach (Opus 4.6 orchestrated stream)
You are an elite basketball performance coach inside the SLAM mobile app.
Hard constraints:
1) Use ONLY the provided mobile app data snapshot for facts.
2) If data is missing, say "Not in current mobile data" and ask for one follow-up.
3) Do not invent medical diagnostics, external records, or third-party stats.
4) Keep responses actionable, concise, and tied to training, recovery, readiness, nutrition, and workload.
5) Output: 1 short summary + 3 bullet actions + 1 risk flag.
`;

const detectMobileViewport = (search) => {
  const params = new URLSearchParams(search);
  return params.get('mobileViewport') === '1' || params.get('mobileEmulator') === '1';
};

const countExercises = (workoutText) => {
  if (!workoutText) return 0;
  const lines = workoutText.split('\n').filter((line) => /^\d+\./.test(line.trim()));
  return lines.length || 5;
};

const parseWorkoutResponse = (data, fitnessLevel, focusAreas) => {
  const meta = data?._meta || {};
  const days = [];
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const cycle = data?.cycle1 || data || {};
  let dayIndex = 0;
  Object.entries(cycle).forEach(([key, value]) => {
    if (key.startsWith('day') && typeof value === 'object') {
      days.push({
        day: dayNames[dayIndex % 7] || `Day ${dayIndex + 1}`,
        focus: value.description || value.strength || 'Training Day',
        warmup: value.warmup || '',
        workout: value.workout || '',
        strength: value.strength || '',
        notes: value.notes || '',
        exercises: countExercises(value.workout),
      });
      dayIndex += 1;
    }
  });

  if (days.length === 0 && Array.isArray(data?.weeks)) {
    data.weeks.forEach((week) => {
      (week?.days || []).forEach((day) => {
        days.push({
          day: day.dayName || `Day ${day.dayNumber || days.length + 1}`,
          focus: day.dayName || day.muscles?.join(', ') || 'Training',
          exercises: day.exercises?.length || 0,
          exerciseList: day.exercises || [],
          estimatedDuration: day.estimatedDuration || '',
        });
      });
    });
  }

  return {
    name: data?.title || `${fitnessLevel} ${focusAreas.join(' + ')} Program`,
    summary: data?.summary || '',
    days,
    raw: data,
    meta,
  };
};

// Map common exercise/focus keywords to SVG muscle IDs
const MUSCLE_KEYWORD_MAP = {
  chest: 'chest', pecs: 'chest', bench: 'chest', 'push-up': 'chest', pushup: 'chest', 'chest press': 'chest',
  back: 'lats', lat: 'lats', row: 'lats', 'pull-up': 'lats', pullup: 'lats', 'chin-up': 'lats',
  shoulder: 'shoulders-anterior', 'overhead press': 'shoulders-anterior', delt: 'shoulders-anterior', press: 'shoulders-anterior',
  quad: 'quadriceps', squat: 'quadriceps', 'leg press': 'quadriceps', lunge: 'quadriceps', 'leg extension': 'quadriceps',
  hamstring: 'hamstrings', 'leg curl': 'hamstrings', deadlift: 'hamstrings', rdl: 'hamstrings',
  glute: 'glutes', hip: 'glutes', 'hip thrust': 'glutes', bridge: 'glutes',
  core: 'abdominals', ab: 'abdominals', plank: 'abdominals', crunch: 'abdominals', sit: 'abdominals',
  tricep: 'triceps', dip: 'triceps', 'skull crusher': 'triceps', pushdown: 'triceps',
  'lower back': 'erectors', erector: 'erectors', 'back extension': 'erectors', hyperextension: 'erectors',
  strength: 'quadriceps', conditioning: 'abdominals', mobility: 'hamstrings',
};

const extractMuscleGroups = (workout) => {
  if (!workout) return [];
  const text = JSON.stringify(workout).toLowerCase();
  const found = new Set();
  for (const [keyword, muscleId] of Object.entries(MUSCLE_KEYWORD_MAP)) {
    if (text.includes(keyword)) found.add(muscleId);
  }
  // Also check focus areas
  const focusText = (workout.days || []).map(d => `${d.focus || ''} ${d.workout || ''} ${d.strength || ''}`).join(' ').toLowerCase();
  for (const [keyword, muscleId] of Object.entries(MUSCLE_KEYWORD_MAP)) {
    if (focusText.includes(keyword)) found.add(muscleId);
  }
  return Array.from(found);
};

const summarizeWorkoutForChat = (workout) => {
  if (!workout) return 'No generated workout yet.';
  return {
    name: workout.name,
    summary: workout.summary || '',
    dayCount: workout.days?.length || 0,
    dayFocus: (workout.days || []).slice(0, 7).map((day) => ({
      day: day.day,
      focus: day.focus,
      exercises: day.exercises || day.exerciseList?.length || 0,
    })),
  };
};

export default function WorkoutGeneratorPage() {
  const location = useLocation();
  const { selectedTeam } = useTeam();
  const isMobileViewport = useMemo(() => detectMobileViewport(location.search), [location.search]);

  const [fitnessLevel, setFitnessLevel] = useState('intermediate');
  const [daysPerWeek, setDaysPerWeek] = useState(4);
  const [numberOfWeeks, setNumberOfWeeks] = useState(4);
  const [focusAreas, setFocusAreas] = useState(['strength', 'conditioning']);
  const [injuries, setInjuries] = useState('');
  const [trainingLocation, setTrainingLocation] = useState('');
  const locationInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const [generatedWorkout, setGeneratedWorkout] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [savingWorkout, setSavingWorkout] = useState(false);
  const [error, setError] = useState(null);

  const [savedWorkouts, setSavedWorkouts] = useState([]);
  const [loadingWorkouts, setLoadingWorkouts] = useState(false);
  const [showMyWorkouts, setShowMyWorkouts] = useState(false);

  const [chatMessages, setChatMessages] = useState([
    {
      id: 'coach-welcome',
      role: 'assistant',
      content: 'Mobile Coach is live. Ask for training edits, readiness pivots, or load adjustments.',
    },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatStreaming, setChatStreaming] = useState(false);
  const [chatError, setChatError] = useState('');
  const chatEndRef = useRef(null);

  const user = getUser();
  const userId = user?.id || user?._id || 'anonymous';
  const teamName = selectedTeam?.teamName || selectedTeam?.team?.team_name || selectedTeam?.team?.full_name || 'Selected Team';

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Google Places Autocomplete initialization
  useEffect(() => {
    if (!locationInputRef.current || autocompleteRef.current) return;

    // Check if Google Maps script is loaded (requires REACT_APP_GOOGLE_MAPS_KEY in env)
    const initAutocomplete = () => {
      if (!window.google?.maps?.places) return;
      try {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          locationInputRef.current,
          {
            types: ['gym', 'health', 'establishment'],
            fields: ['formatted_address', 'name', 'place_id', 'types'],
          }
        );
        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          if (place?.name) {
            setTrainingLocation(place.formatted_address ? `${place.name}, ${place.formatted_address}` : place.name);
          }
        });
      } catch (err) {
        console.log('[Workout] Google Places not available, using plain text input');
      }
    };

    // If Google Maps is already loaded
    if (window.google?.maps?.places) {
      initAutocomplete();
    } else {
      // Load Google Maps script if API key is available
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
      if (apiKey && !document.querySelector('script[src*="maps.googleapis.com"]')) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.onload = initAutocomplete;
        document.head.appendChild(script);
      }
    }
  }, []);

  const loadSavedWorkouts = useCallback(async () => {
    setLoadingWorkouts(true);
    try {
      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/user/${userId}/workouts`);
      if (!response.ok) throw new Error('Failed to load workouts');
      const data = await response.json();
      setSavedWorkouts(data.result || []);
    } catch (err) {
      console.error('[Workout] Failed to load workouts:', err);
    } finally {
      setLoadingWorkouts(false);
    }
  }, [userId]);

  useEffect(() => {
    loadSavedWorkouts();
  }, [loadSavedWorkouts]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/generate-workout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          numberOfDays: daysPerWeek,
          numberOfCycles: numberOfWeeks,
          fitnessLevel,
          focusAreas,
          injuries: injuries || undefined,
          prescribedExercises: focusAreas.includes('sport-specific') ? 'sport-specific athletic drills' : '',
          trainingLocation: trainingLocation || undefined,
          numberOfWeeks,
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || `Server error (${response.status})`);
      }

      const data = await response.json();
      const workoutData = data.data || data;
      setGeneratedWorkout(parseWorkoutResponse(workoutData, fitnessLevel, focusAreas));
      setExpandedDay(null);
    } catch (err) {
      console.error('[Workout] Generation error:', err);
      setError(err.message || 'Failed to generate workout.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveWorkout = async () => {
    if (!generatedWorkout) return;
    setSavingWorkout(true);
    setError(null);

    try {
      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/user/${userId}/workouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: generatedWorkout.name,
          fitnessLevel,
          daysPerWeek,
          numberOfWeeks,
          trainingLocation,
          focusAreas,
          injuries,
          days: generatedWorkout.days,
          raw: generatedWorkout.raw,
        }),
      });
      if (!response.ok) throw new Error('Failed to save workout');
      const payload = await response.json();
      setSavedWorkouts((prev) => [payload.result, ...prev]);
    } catch (err) {
      console.error('[Workout] Save error:', err);
      setError(err.message || 'Failed to save workout.');
    } finally {
      setSavingWorkout(false);
    }
  };

  const handleLoadWorkout = (workout) => {
    setGeneratedWorkout({
      name: workout.name || 'Saved Workout',
      days: workout.days || [],
      raw: workout.raw || {},
      summary: workout.summary || '',
    });
    setFitnessLevel(workout.fitnessLevel || 'intermediate');
    setDaysPerWeek(workout.daysPerWeek || 4);
    setNumberOfWeeks(workout.numberOfWeeks || 4);
    setTrainingLocation(workout.trainingLocation || '');
    setFocusAreas(workout.focusAreas || ['strength']);
    setShowMyWorkouts(false);
  };

  const toggleFocus = (focusId) => {
    setFocusAreas((prev) => (
      prev.includes(focusId)
        ? prev.filter((focus) => focus !== focusId)
        : [...prev, focusId]
    ));
  };

  const buildScopedCoachMessage = (userMessage) => {
    const mobileDataSnapshot = {
      team: teamName,
      fitnessLevel,
      daysPerWeek,
      numberOfWeeks,
      trainingLocation: trainingLocation || 'not specified',
      focusAreas,
      injuries: injuries || 'none',
      workout: summarizeWorkoutForChat(generatedWorkout),
      savedWorkoutCount: savedWorkouts.length,
    };

    return `MOBILE APP DATA SNAPSHOT (ONLY SOURCE OF TRUTH):
${JSON.stringify(mobileDataSnapshot, null, 2)}

USER REQUEST:
${userMessage}`;
  };

  const handleCoachSend = async () => {
    const message = chatInput.trim();
    if (!message || chatStreaming) return;

    const userMessage = { id: `u-${Date.now()}`, role: 'user', content: message };
    setChatMessages((prev) => [...prev, userMessage]);
    setChatInput('');
    setChatStreaming(true);
    setChatError('');

    const assistantId = `a-${Date.now() + 1}`;
    setChatMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: '' }]);

    try {
      const updateAssistantMessage = (content) => {
        setChatMessages((prev) => prev.map((msg) => (msg.id === assistantId ? { ...msg, content } : msg)));
      };

      const history = chatMessages
        .filter((msg) => msg.id !== 'coach-welcome')
        .map((msg) => ({ role: msg.role, content: msg.content }));

      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/streaming-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          systemPrompt: MOBILE_COACH_SYSTEM_PROMPT,
          message: buildScopedCoachMessage(message),
          history,
          userProfile: {
            fitnessLevel,
            goals: focusAreas,
            frequency: daysPerWeek,
            injuries: injuries || 'none',
            timePerSession: '45-70',
            equipment: ['Bodyweight', 'Basketball', 'Dumbbells'],
          },
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error(`Stream failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let nextContent = accumulated;
        let streamError = null;

        lines.forEach((line) => {
          if (!line.startsWith('data: ')) return;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.type === 'chunk' && payload.content) {
              nextContent += payload.content;
            }
            if (payload.type === 'complete' && payload.fullContent) {
              nextContent = payload.fullContent;
            }
            if (payload.type === 'error') {
              streamError = payload.error || 'Streaming error';
            }
          } catch (parseError) {
            // Ignore malformed streaming lines.
          }
        });

        if (nextContent !== accumulated) {
          accumulated = nextContent;
          updateAssistantMessage(accumulated);
        }

        if (streamError) {
          throw new Error(streamError);
        }
      }
    } catch (err) {
      console.error('[Workout] Coach stream error:', err);
      setChatError(err.message || 'Coach response failed');
      setChatMessages((prev) => prev.map((msg) => (
        msg.id === assistantId
          ? { ...msg, content: 'Unable to stream coach response right now. Please retry.' }
          : msg
      )));
    } finally {
      setChatStreaming(false);
    }
  };

  const cardClass = 'rounded-2xl border border-white/10 bg-[#0b1118]';
  const compactClass = isMobileViewport ? 'p-3' : 'p-4';

  return (
    <div className={`min-h-full bg-[#040608] text-zinc-100 ${isMobileViewport ? 'p-2 pb-16' : 'p-4 md:p-6'}`}>
      <div className={`${isMobileViewport ? 'space-y-2' : 'mx-auto max-w-6xl space-y-4'}`}>
        <header className={`${cardClass} ${compactClass}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-xl border border-orange-400/45 bg-orange-400/10 text-orange-300">
                <Dumbbell size={16} />
              </span>
              <div>
                <h1 className="text-[15px] font-semibold tracking-tight">Workout Generator</h1>
                <p className="text-[10px] text-zinc-500">{teamName} • mobile-scoped programming</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowMyWorkouts((prev) => !prev)}
              className="inline-flex items-center gap-1 rounded-full border border-white/20 px-2 py-1 text-[10px] text-zinc-300 hover:text-white"
            >
              <Calendar size={11} />
              Saved ({savedWorkouts.length})
            </button>
          </div>
        </header>

        {(error || chatError) && (
          <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200">
            {error || chatError}
          </div>
        )}

        <div className="grid gap-2 lg:grid-cols-[1.15fr_.85fr]">
          <section className="space-y-2">
            {showMyWorkouts && (
              <div className={`${cardClass} ${compactClass}`}>
                <p className="mb-2 text-[11px] font-medium text-zinc-300">My Saved Workouts</p>
                {loadingWorkouts ? (
                  <div className="flex items-center gap-2 text-zinc-500 text-[11px]">
                    <Loader2 size={14} className="animate-spin" />
                    Loading...
                  </div>
                ) : savedWorkouts.length === 0 ? (
                  <p className="text-[11px] text-zinc-500">No saved workouts yet.</p>
                ) : (
                  <div className="max-h-48 space-y-1 overflow-y-auto">
                    {savedWorkouts.map((workout, index) => (
                      <button
                        type="button"
                        key={workout.id || index}
                        onClick={() => handleLoadWorkout(workout)}
                        className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-left hover:bg-white/[0.06]"
                      >
                        <p className="text-[12px] text-zinc-100">{workout.name || 'Untitled Workout'}</p>
                        <p className="text-[10px] text-zinc-500">
                          {(workout.daysPerWeek || workout.days?.length || '?')} days/week • {workout.fitnessLevel || 'custom'}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!generatedWorkout ? (
              <div className={`${cardClass} ${compactClass} space-y-3`}>
                <div>
                  <p className="mb-1 flex items-center gap-1 text-[11px] text-zinc-400"><Target size={12} /> Fitness Level</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {FITNESS_LEVELS.map((level) => (
                      <button
                        type="button"
                        key={level.id}
                        onClick={() => setFitnessLevel(level.id)}
                        className={`rounded-xl border px-2 py-2 text-left ${
                          fitnessLevel === level.id
                            ? 'border-orange-400/70 bg-orange-400/10'
                            : 'border-white/10 bg-white/[0.02]'
                        }`}
                      >
                        <p className="text-[12px] text-zinc-100">{level.label}</p>
                        <p className="text-[10px] text-zinc-500">{level.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-1 flex items-center gap-1 text-[11px] text-zinc-400"><Calendar size={12} /> Days / Week</p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {[3, 4, 5, 6].map((days) => (
                      <button
                        type="button"
                        key={days}
                        onClick={() => setDaysPerWeek(days)}
                        className={`rounded-lg border py-1.5 text-[11px] font-medium ${
                          daysPerWeek === days
                            ? 'border-orange-400/70 bg-orange-400/15 text-orange-200'
                            : 'border-white/10 bg-white/[0.02] text-zinc-400'
                        }`}
                      >
                        {days}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-1 flex items-center gap-1 text-[11px] text-zinc-400"><Calendar size={12} /> Program Duration</p>
                  <div className="grid grid-cols-5 gap-1.5">
                    {[2, 4, 6, 8, 12].map((weeks) => (
                      <button
                        type="button"
                        key={weeks}
                        onClick={() => setNumberOfWeeks(weeks)}
                        className={`rounded-lg border py-1.5 text-[11px] font-medium ${
                          numberOfWeeks === weeks
                            ? 'border-orange-400/70 bg-orange-400/15 text-orange-200'
                            : 'border-white/10 bg-white/[0.02] text-zinc-400'
                        }`}
                      >
                        {weeks}w
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-1 flex items-center gap-1 text-[11px] text-zinc-400"><Users size={12} /> Focus Areas</p>
                  <div className="flex flex-wrap gap-1.5">
                    {FOCUS_OPTIONS.map((option) => {
                      const selected = focusAreas.includes(option.id);
                      return (
                        <button
                          type="button"
                          key={option.id}
                          onClick={() => toggleFocus(option.id)}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] ${
                            selected
                              ? 'border-orange-400/70 bg-orange-400/15 text-orange-200'
                              : 'border-white/15 bg-white/[0.02] text-zinc-400'
                          }`}
                        >
                          {selected && <Check size={10} />}
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <p className="mb-1 flex items-center gap-1 text-[11px] text-zinc-400"><MapPin size={12} /> Training Location</p>
                  <input
                    ref={locationInputRef}
                    type="text"
                    value={trainingLocation}
                    onChange={(event) => setTrainingLocation(event.target.value)}
                    placeholder="e.g. Gold's Gym, UCLA Weight Room"
                    className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] text-zinc-100 placeholder:text-zinc-500 focus:border-orange-400/60 focus:outline-none"
                  />
                </div>

                <div>
                  <p className="mb-1 text-[11px] text-zinc-400">Injury / Limitation Notes</p>
                  <input
                    type="text"
                    value={injuries}
                    onChange={(event) => setInjuries(event.target.value)}
                    placeholder="e.g. left knee load cap"
                    className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] text-zinc-100 placeholder:text-zinc-500 focus:border-orange-400/60 focus:outline-none"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={isGenerating || focusAreas.length === 0}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-orange-400/50 bg-gradient-to-r from-orange-500/85 to-amber-500/70 py-2 text-[12px] font-semibold text-white disabled:opacity-45"
                >
                  {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {isGenerating ? 'Generating...' : 'Generate Program'}
                </button>
              </div>
            ) : (
              <div className={`${cardClass} ${compactClass} space-y-2`}>
                <div className="rounded-xl border border-orange-400/35 bg-orange-400/10 px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[13px] font-semibold text-zinc-100">{generatedWorkout.name}</p>
                    <span className="rounded-full border border-orange-400/40 px-2 py-0.5 text-[10px] text-orange-200">
                      {(generatedWorkout.days?.length || daysPerWeek)} days
                    </span>
                  </div>
                  <p className="mt-1 text-[10px] text-zinc-400">
                    {fitnessLevel} • {numberOfWeeks}wk • {focusAreas.join(', ')}{trainingLocation ? ` • ${trainingLocation}` : ''}{injuries ? ` • ${injuries}` : ''}
                  </p>
                </div>

                <div className="space-y-1.5">
                  {(generatedWorkout.days || []).map((day, index) => {
                    const open = expandedDay === index;
                    return (
                      <div key={`${day.day}-${index}`} className="rounded-xl border border-white/10 bg-white/[0.02]">
                        <button
                          type="button"
                          onClick={() => setExpandedDay(open ? null : index)}
                          className="flex w-full items-center justify-between px-3 py-2 text-left"
                        >
                          <div>
                            <p className="text-[12px] text-zinc-100">{day.day}</p>
                            <p className="text-[10px] text-zinc-500">{day.focus}</p>
                          </div>
                          {open ? <ChevronUp size={13} className="text-zinc-400" /> : <ChevronDown size={13} className="text-zinc-400" />}
                        </button>
                        {open && (
                          <div className="space-y-1 border-t border-white/10 px-3 py-2 text-[10px] text-zinc-300">
                            {day.warmup && <p><span className="text-orange-300">Warm-up:</span> {day.warmup}</p>}
                            {day.workout && <p><span className="text-orange-300">Workout:</span> {day.workout}</p>}
                            {day.strength && <p><span className="text-orange-300">Strength:</span> {day.strength}</p>}
                            {day.notes && <p><span className="text-zinc-500">Notes:</span> {day.notes}</p>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Muscle Visualizer -- progressive disclosure: shows after workout is generated */}
                {extractMuscleGroups(generatedWorkout).length > 0 && (
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                    <p className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-zinc-300">
                      <Target size={12} className="text-orange-300" /> Targeted Muscle Groups
                    </p>
                    <MuscleVisualizer
                      muscleGroups={extractMuscleGroups(generatedWorkout)}
                      view="split"
                      highlightColor="hsl(25 95% 53%)"
                      inactiveColor="hsl(0 0% 20%)"
                      interactive
                    />
                    <div className="mt-2 flex flex-wrap gap-1">
                      {extractMuscleGroups(generatedWorkout).map(m => (
                        <span key={m} className="rounded-full border border-orange-400/30 bg-orange-400/10 px-2 py-0.5 text-[9px] text-orange-200">
                          {m.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setGeneratedWorkout(null);
                      setExpandedDay(null);
                    }}
                    className="rounded-xl border border-white/15 bg-white/[0.04] py-2 text-[11px] text-zinc-200"
                  >
                    Regenerate
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveWorkout}
                    disabled={savingWorkout}
                    className="inline-flex items-center justify-center gap-1 rounded-xl border border-orange-400/45 bg-orange-500/15 py-2 text-[11px] text-orange-200 disabled:opacity-50"
                  >
                    {savingWorkout ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    Save
                  </button>
                </div>
              </div>
            )}
          </section>

          <aside className={`${cardClass} ${compactClass} flex min-h-[360px] flex-col`}>
            <div className="mb-2 flex items-center justify-between gap-2">
              <div>
                <p className="text-[12px] font-medium text-zinc-100">Coach Chat</p>
                <p className="text-[10px] text-zinc-500">Opus 4.6 orchestrated streaming • mobile-data scope</p>
              </div>
              {chatStreaming && <Loader2 size={13} className="animate-spin text-orange-300" />}
            </div>

            <div className="flex-1 space-y-1 overflow-y-auto rounded-xl border border-white/10 bg-black/20 px-2 py-2">
              {chatMessages.map((message) => (
                <div
                  key={message.id}
                  className={`max-w-[94%] rounded-xl px-2 py-1.5 text-[11px] leading-relaxed ${
                    message.role === 'user'
                      ? 'ml-auto border border-orange-400/45 bg-orange-500/15 text-orange-100'
                      : 'border border-white/10 bg-white/[0.04] text-zinc-200'
                  }`}
                >
                  {message.content || (chatStreaming ? '...' : '')}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            <div className="mt-2 flex gap-1.5">
              <input
                type="text"
                value={chatInput}
                onChange={(event) => setChatInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleCoachSend();
                }}
                placeholder="Ask about readiness, load, or session edits"
                className="flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-2.5 py-2 text-[11px] text-zinc-100 placeholder:text-zinc-500 focus:border-orange-400/60 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleCoachSend}
                disabled={!chatInput.trim() || chatStreaming}
                className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-xl border border-orange-400/55 bg-orange-500/15 text-orange-200 disabled:opacity-45"
              >
                <Send size={13} />
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
